import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SquaresFour, Users, FileText, Receipt, CheckSquare, Bell, SignOut, CalendarBlank, GearSix, ClipboardText, Notebook, Drop, Cookie, Cigarette, IdentificationCard, Storefront, CaretDown, CaretRight, Megaphone, Tag, CalendarCheck, Clock, Hammer, Gift, Buildings, Baby, FirstAidKit, UserCircle, List, X, CookingPot, UserPlus, UserMinus, ShieldWarning, Handshake, ForkKnife, BuildingOffice, CaretDoubleLeft, CaretDoubleRight, UmbrellaSimple, Key, Scroll, IdentificationBadge, PaperPlaneRight, ChatCircleDots, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useState, useEffect, useSyncExternalStore } from 'react';
import { api } from '../../App';

// External store pattern — bypasses react-hooks/set-state-in-effect rule.
// A single background poller updates _counters; components subscribe via useSyncExternalStore.
const _counters = { pending: 0, viberUnread: 0, listeners: new Set() };
const _emit = () => { _counters.listeners.forEach(fn => fn()); };
const _subscribe = (fn) => { _counters.listeners.add(fn); return () => _counters.listeners.delete(fn); };
let _pollInterval = null;
const _ensurePolling = () => {
  if (_pollInterval) return;
  const fetchAll = () => {
    api.get('/tasks').then(r => {
      const c = r.data.filter(t => t.status !== 'zavrseno').length;
      if (c !== _counters.pending) { _counters.pending = c; _emit(); }
    }).catch(() => {});
    api.get('/viber-chat/conversations').then(r => {
      const c = (r.data.items || []).reduce((s, x) => s + (x.unread || 0), 0);
      if (c !== _counters.viberUnread) { _counters.viberUnread = c; _emit(); }
    }).catch(() => {});
  };
  fetchAll();
  _pollInterval = setInterval(fetchAll, 20000);
};
const usePendingTasksCount = () => useSyncExternalStore(_subscribe, () => _counters.pending, () => 0);
const useViberUnreadCount = () => useSyncExternalStore(_subscribe, () => _counters.viberUnread, () => 0);

export default function MainLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [zahtjeviOpen, setZahtjeviOpen] = useState(
    location.pathname.startsWith('/zahtjev')
  );
  const pendingTasks = usePendingTasksCount();
  const viberUnread = useViberUnreadCount();
  _ensurePolling();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopHidden, setDesktopHidden] = useState(() => localStorage.getItem('sidebarHidden') === '1');

  const toggleDesktop = () => {
    setDesktopHidden(prev => {
      const next = !prev;
      localStorage.setItem('sidebarHidden', next ? '1' : '0');
      return next;
    });
  };

  // Close sidebar on route change (mobile) — handled inline at click time on each Link.
  // (No effect — to avoid setState-in-effect rule.)

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Uspješno ste se odjavili');
    navigate('/login');
  };

  const mainNavItems = [
    { path: '/dnevnik', icon: Notebook, label: 'Dnevnik rada', testid: 'nav-dnevnik' },
    { path: '/tasks', icon: CheckSquare, label: 'Zadaci', testid: 'nav-tasks' },
    { path: '/vat-monthly', icon: CalendarBlank, label: 'Mjesečne PDV Tabele', testid: 'nav-vat-monthly' },
    { path: '/ioppd', icon: ClipboardText, label: 'IOPPD evidencija', testid: 'nav-ioppd' },
    { path: '/prijava-radnika', icon: UserPlus, label: 'Prijava radnika', testid: 'nav-prijava-radnika' },
    { path: '/odjava-radnika', icon: UserMinus, label: 'Odjava radnika', testid: 'nav-odjava-radnika' },
    // Zahtjevi (collapsible group) - renderovan zasebno između Prijava radnika i Ugovori
    { path: '/ugovori', icon: Handshake, label: 'Ugovori', testid: 'nav-ugovori' },
    { path: '/irms-zahtjevi', icon: Buildings, label: 'IRMS Zahtjevi', testid: 'nav-irms' },
    { path: '/tokeni', icon: Key, label: 'Tokeni', testid: 'nav-tokeni' },
    { path: '/rsv', icon: IdentificationBadge, label: 'RSV', testid: 'nav-rsv' },
    { path: '/godisnje-obaveze', icon: CalendarCheck, label: 'Godišnje obaveze', testid: 'nav-godisnje' },
    { path: '/porodiljsko', icon: Baby, label: 'Porodiljsko', testid: 'nav-porodiljsko' },
    { path: '/bolovanje', icon: FirstAidKit, label: 'Bolovanje', testid: 'nav-bolovanje' },
    { path: '/clients', icon: Users, label: 'Klijenti', testid: 'nav-clients' },
    { path: '/fizicka-lica', icon: UserCircle, label: 'Fizička lica', testid: 'nav-fizicka-lica' },
    { path: '/cestitke', icon: Gift, label: 'Čestitke', testid: 'nav-cestitke' },
    { path: '/', icon: SquaresFour, label: 'Dashboard', testid: 'nav-dashboard' },
    { path: '/hronologija', icon: FileText, label: 'Hronologija', testid: 'nav-hronologija' },
    { path: '/sent-emails', icon: PaperPlaneRight, label: 'Poslati emailovi', testid: 'nav-sent-emails' },
  ];

  const zahtjeviItems = [
    { path: '/zahtjev-brisevi', icon: Cookie, label: 'Brisevi', testid: 'nav-zahtjev-brisevi' },
    { path: '/zahtjev-voda', icon: Drop, label: 'INSTITUT VODA', testid: 'nav-zahtjev-voda' },
    { path: '/zahtjev-hrana', icon: ForkKnife, label: 'INSTITUT HRANA', testid: 'nav-zahtjev-hrana' },
    { path: '/zahtjev-duvanski', icon: Cigarette, label: 'Duvanski proizvodi', testid: 'nav-zahtjev-duvanski' },
    { path: '/zahtjev-kaznena', icon: IdentificationCard, label: 'Kaznena evidencija', testid: 'nav-zahtjev-kaznena' },
    { path: '/zahtjev-prijava-trgovine', icon: Storefront, label: 'Prijava trgovine', testid: 'nav-zahtjev-prijava-trgovine' },
    { path: '/zahtjev-prijava-zanatstva', icon: Hammer, label: 'Prijava zanatstva', testid: 'nav-zahtjev-prijava-zanatstva' },
    { path: '/zahtjev-prigovor', icon: Megaphone, label: 'Prigovor potrošača', testid: 'nav-zahtjev-prigovor' },
    { path: '/zahtjev-pogodnosti', icon: Tag, label: 'Prodajne pogodnosti', testid: 'nav-zahtjev-pogodnosti' },
    { path: '/zahtjev-odmor', icon: CalendarCheck, label: 'Sedmični odmor', testid: 'nav-zahtjev-odmor' },
    { path: '/zahtjev-smjene', icon: Clock, label: 'Raspored smjena', testid: 'nav-zahtjev-smjene' },
    { path: '/zahtjev-registracija-hrane', icon: CookingPot, label: 'Registracija hrane', testid: 'nav-zahtjev-reg-hrane' },
    { path: '/zahtjev-ugostiteljska', icon: ForkKnife, label: 'Ugostiteljska djelatnost', testid: 'nav-zahtjev-ugostiteljska' },
    { path: '/zahtjev-ctr', icon: BuildingOffice, label: 'CTR - Ugostiteljski objekti', testid: 'nav-zahtjev-ctr' },
    { path: '/zahtjev-ctr-restorani', icon: BuildingOffice, label: 'CTR - Restorani / Barovi', testid: 'nav-zahtjev-ctr-restorani' },
    { path: '/zahtjev-ug-opstina', icon: Storefront, label: 'Ugostiteljska - Opština', testid: 'nav-zahtjev-ug-opstina' },
    { path: '/zahtjev-kupalista', icon: UmbrellaSimple, label: 'Kupališta - turističke usluge', testid: 'nav-zahtjev-kupalista' },
    { path: '/zahtjev-punomocje-coreit', icon: Scroll, label: 'Punomoćje za token COREIT', testid: 'nav-zahtjev-punomocje-coreit' },
    { path: '/mobing', icon: ShieldWarning, label: 'Mobing', testid: 'nav-mobing' },
  ];

  const bottomNavItems = [
    { path: '/notifications', icon: Bell, label: 'Obavještenja', testid: 'nav-notifications' },
    ...(user.role === 'super_admin' ? [{ path: '/workspaces', icon: Buildings, label: 'Agencije', testid: 'nav-workspaces' }] : []),
    ...(user.role === 'super_admin' ? [{ path: '/backup', icon: FloppyDisk, label: 'Backup & Restore', testid: 'nav-backup' }] : []),
    ...(user.role === 'admin' || user.role === 'super_admin' ? [{ path: '/settings', icon: GearSix, label: 'Podešavanja', testid: 'nav-settings' }] : []),
  ];

  const isZahtjevActive = location.pathname.startsWith('/zahtjev') || location.pathname === '/mobing';

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-300 border-r border-slate-800 flex-col transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0 flex' : '-translate-x-full flex lg:translate-x-0'} ${desktopHidden ? 'lg:hidden' : 'lg:flex'}`} data-testid="sidebar">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex-1 h-16 flex items-center justify-center">
            <img 
              src="/sidebar-logo.png"
              alt="CRM Finance Logo"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden ml-2 p-1 hover:bg-slate-800 rounded">
            <X size={20} className="text-slate-400" />
          </button>
          <button onClick={toggleDesktop} className="hidden lg:block ml-2 p-1 hover:bg-slate-800 rounded" title="Sakrij meni" data-testid="sidebar-hide-btn">
            <CaretDoubleLeft size={18} className="text-slate-400" />
          </button>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-3">
          {/* Main nav */}
          {mainNavItems.map(item => {
            const Icon = item.icon;
            const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path} data-testid={item.testid}
                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${isActive ? 'bg-blue-600/20 text-blue-400 border-r-2 border-blue-400' : 'hover:bg-slate-800 hover:text-white'}`}>
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                <span className="flex-1">{item.label}</span>
                {item.path === '/tasks' && pendingTasks > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5">{pendingTasks}</span>
                )}
              </Link>
            );
          })}

          {/* Zahtjevi dropdown */}
          <button onClick={() => setZahtjeviOpen(!zahtjeviOpen)} data-testid="nav-zahtjevi-toggle"
            className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${isZahtjevActive ? 'bg-blue-600/10 text-blue-400' : 'hover:bg-slate-800 hover:text-white'}`}>
            <ClipboardText size={20} weight={isZahtjevActive ? 'fill' : 'regular'} />
            <span className="flex-1 text-left">Zahtjevi</span>
            {zahtjeviOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
          </button>
          {zahtjeviOpen && (
            <div className="bg-slate-950/50">
              {zahtjeviItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} data-testid={item.testid}
                    className={`flex items-center gap-3 pl-10 pr-5 py-2 text-xs transition-colors ${isActive ? 'bg-blue-600/20 text-blue-400 border-r-2 border-blue-400' : 'hover:bg-slate-800 hover:text-white text-slate-400'}`}>
                    <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Viber — direct link (single page) */}
          <Link to="/viber-chat" data-testid="nav-viber"
            className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${location.pathname === '/viber-chat' ? 'bg-purple-600/20 text-purple-300 border-r-2 border-purple-400' : 'hover:bg-slate-800 hover:text-white'}`}>
            <ChatCircleDots size={20} weight={location.pathname === '/viber-chat' ? 'fill' : 'regular'} />
            <span className="flex-1">Viber</span>
            {viberUnread > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5" data-testid="viber-unread-badge">
                {viberUnread > 99 ? '99+' : viberUnread}
              </span>
            )}
          </Link>

          {/* Bottom nav */}
          {bottomNavItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path} data-testid={item.testid}
                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${isActive ? 'bg-blue-600/20 text-blue-400 border-r-2 border-blue-400' : 'hover:bg-slate-800 hover:text-white'}`}>
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white">{user.name || 'Korisnik'}</p>
              <p className="text-xs text-slate-400 capitalize">{(user.role === 'admin' || user.role === 'super_admin') ? 'Administrator' : user.role === 'supervizor' ? 'Supervizor' : 'Zaposleni'}</p>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-slate-800 rounded-lg transition-colors" title="Odjava" data-testid="logout-button">
              <SignOut size={20} className="text-slate-400" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        {/* Desktop "Show menu" floating button */}
        {desktopHidden && (
          <button
            onClick={toggleDesktop}
            className="hidden lg:flex fixed top-4 left-4 z-40 items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg border border-slate-700 transition-all"
            title="Prikaži meni"
            data-testid="sidebar-show-btn"
          >
            <CaretDoubleRight size={18} />
            <span className="text-xs font-medium">Meni</span>
          </button>
        )}
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="mobile-menu-btn">
            <List size={24} className="text-slate-700" />
          </button>
          <img src="/sidebar-logo.png" alt="Logo" className="h-8 object-contain" />
        </div>
        <div className={`p-4 lg:p-8 max-w-7xl mx-auto ${desktopHidden ? 'lg:pl-24' : ''}`}>
          {children}
        </div>
      </main>
    </div>
  );
}
